import { useState, useEffect, useCallback, useRef } from 'react';
import { skillsAPI, conexionesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Wrench, Plus, Trash2, Loader2, Save, AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
  PlayCircle, XCircle, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import ImportarCurl from '@/components/ImportarCurl';
import PegarLoQueTengas from '@/components/PegarLoQueTengas';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * La URL completa de una herramienta, a partir de la base de su conexión y su ruta.
 *
 * **El validador la exige absoluta.** Una ruta suelta como `/libro-banco` se interpreta como
 * relativa y no resuelve, y el error aparecería recién al ejecutarla — en el turno de un agente,
 * que es donde menos se diagnostica.
 */
function componerLaUrl(base, ruta) {
  const b = String(base || '').replace(/\/+$/, '');
  const r = String(ruta || '');
  if (!r) return b;
  if (/^https?:\/\//i.test(r)) return r;      // ya venía completa
  if (!b) return r;                            // sin base, se deja lo que hay y el validador avisa
  return b + (r.startsWith('/') ? r : `/${r}`);
}

const METODOS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];
const RIESGOS = [
  { v: 'read', t: 'lectura' },
  { v: 'write', t: 'escritura' },
  { v: 'external', t: 'externa' },
  { v: 'destructive', t: 'destructiva' },
];
const UBICACIONES = ['ruta', 'query', 'cuerpo', 'header'];
const QUIEN = [
  { v: 'modelo', t: 'el modelo' },
  { v: 'runtime', t: 'el runtime' },
  { v: 'conexion', t: 'la conexion' },
];
const TIPOS = ['texto', 'entero', 'decimal', 'booleano', 'lista', 'objeto'];
const MODOS_AUTH = [
  'ninguna', 'bearer', 'basic', 'api_key_header', 'api_key_query',
  'oauth2_client_credentials', 'omnisys_interno',
];

// Las cinco consecuencias, en el orden en que se declaran: de la mas comun a la mas grave.
// Los nombres son fijos (viven en el codigo); quien autoriza cada una lo decide la capsula.
const CONSECUENCIAS = [
  { k: 'cambia_datos', t: 'Cambia datos', d: 'Deja el sistema remoto distinto' },
  { k: 'alcanza_a_muchos', t: 'Alcanza a muchos', d: 'Afecta mas de un registro por llamada' },
  { k: 'crea_acceso', t: 'Crea acceso', d: 'Da o amplia la capacidad de alguien de entrar o hacer' },
  { k: 'no_se_puede_deshacer', t: 'No se puede deshacer', d: 'No hay operacion inversa desde aca' },
  { k: 'mueve_dinero', t: 'Mueve dinero', d: 'Compromete o transfiere valor' },
];

const HERRAMIENTA_NUEVA = {
  nombre: '', descripcion: '', metodo: 'GET', url: '', parametros: [],
  headers_fijos: {}, auth: { modo: 'ninguna', conexion_id: '', nombre_del_campo: '' },
  riesgo: 'read', requiere_aprobacion: false, verificar_tls: true, motivo_sin_tls: '',
  consecuencias: { declaradas: false },
  timeout_seg: 30, max_respuesta_kb: 256, activa: true,
};

/**
 * El editor de `tools[]` de una skill.
 *
 * **Por que existe.** Sin el, las puertas de carga no llegan a ningun lado: el importador de
 * `curl` devolvia una herramienta y no habia donde ponerla. El criterio de aceptacion del plan
 * —cinco minutos, alguien que no programa, sin ayuda— no se puede ni cronometrar sin esto.
 *
 * **La decision que ordena la pantalla: el error aparece mientras se escribe, no al guardar.**
 * El validador ya devuelve todos los errores con el campo al que apuntan; mostrarlos recien al
 * apretar Guardar convierte cada correccion en un viaje de ida y vuelta, y asi es como se
 * abandona un formulario. El backend igual valida —la garantia no puede vivir en la pantalla—
 * pero para cuando alguien llegue a Guardar no deberia quedar nada que decir.
 */
export default function EditorDeHerramientas({ skill, tenantId, capsuleId, onGuardada }) {
  const [tools, setTools] = useState([]);
  const [abierta, setAbierta] = useState(null);
  const [errores, setErrores] = useState([]);
  const [conexiones, setConexiones] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    setTools(Array.isArray(skill?.tools) ? skill.tools : []);
    setAbierta(null);
    setErrores([]);
    setSucio(false);
  }, [skill?.skill_id]);

  useEffect(() => {
    if (!tenantId) return;
    conexionesAPI.list(tenantId, capsuleId || '')
      .then(({ data }) => setConexiones(data || []))
      .catch(() => setConexiones([]));
  }, [tenantId, capsuleId]);

  // El linter en vivo, con freno: una llamada por tecla seria ruido y carga inutil.
  const revalidar = useCallback((lista) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      if (!lista.length) { setErrores([]); return; }
      try {
        const { data } = await skillsAPI.validarHerramientas({
          tools: lista, tenant_id: tenantId || '', capsule_id: capsuleId || '',
        });
        setErrores(data.errores || []);
      } catch { /* el linter no puede romper la pantalla */ }
    }, 400);
  }, [tenantId, capsuleId]);

  const cambiar = (lista) => { setTools(lista); setSucio(true); revalidar(lista); };

  const editar = (i, campo, valor) => {
    const lista = tools.map((t, j) => (j === i ? { ...t, [campo]: valor } : t));
    cambiar(lista);
  };

  const editarParam = (i, j, campo, valor) => {
    const lista = tools.map((t, k) => {
      if (k !== i) return t;
      const params = (t.parametros || []).map((p, m) => (m === j ? { ...p, [campo]: valor } : p));
      return { ...t, parametros: params };
    });
    cambiar(lista);
  };

  const erroresDe = (nombre, indice) =>
    errores.filter((e) => e.herramienta === (nombre || `#${indice + 1}`));

  const guardar = async () => {
    setGuardando(true);
    try {
      await skillsAPI.update(skill.skill_id, { tools, capsule_id: capsuleId || '' });
      toast.success('Herramientas guardadas');
      setSucio(false);
      if (onGuardada) onGuardada(tools);
    } catch (err) {
      const detalle = err?.response?.data?.detail;
      if (detalle?.errores) {
        setErrores(detalle.errores);
        toast.error('Hay cosas que corregir antes de guardar');
      } else {
        toast.error(formatApiError(err) || 'No se pudo guardar');
      }
    } finally {
      setGuardando(false);
    }
  };

  const agregar = (herramienta) => {
    const lista = [...tools, herramienta];
    cambiar(lista);
    setAbierta(lista.length - 1);
  };

  return (
    <Card className="rounded-sm border-brand-border" data-testid="editor-herramientas">
      <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-center justify-between">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600 flex items-center gap-1">
          <Wrench className="w-3 h-3" /> Herramientas ({tools.length})
        </CardTitle>
        <div className="flex items-center gap-2">
          {errores.length > 0 && (
            <Badge variant="outline" className="text-[9px] rounded-sm text-red-600 border-red-200 gap-1">
              <AlertCircle className="w-3 h-3" /> {errores.length} por corregir
            </Badge>
          )}
          {sucio && errores.length === 0 && (
            <Badge variant="outline" className="text-[9px] rounded-sm text-green-700 border-green-200 gap-1">
              <CheckCircle2 className="w-3 h-3" /> listo para guardar
            </Badge>
          )}
          <Button size="sm" variant="outline" data-testid="agregar-herramienta"
                  className="h-7 text-[10px] rounded-sm gap-1"
                  onClick={() => agregar({ ...HERRAMIENTA_NUEVA })}>
            <Plus className="w-3 h-3" /> En blanco
          </Button>
          <Button size="sm" data-testid="guardar-herramientas" disabled={guardando || !sucio}
                  className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
                  onClick={guardar}>
            {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Guardar
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-3 space-y-3">
        {tools.length === 0 && (
          <p className="text-[10px] text-zinc-400">
            Esta skill no declara herramientas. Pega un curl abajo, o empeza en blanco.
          </p>
        )}

        {tools.map((t, i) => {
          const mios = erroresDe(t.nombre, i);
          const abiertaEsta = abierta === i;
          return (
            <div key={i} className="border border-brand-border rounded-sm" data-testid={`herramienta-${i}`}>
              <div className="flex items-center justify-between px-3 py-2">
                <button className="flex items-center gap-2 text-left flex-1"
                        onClick={() => setAbierta(abiertaEsta ? null : i)}>
                  {abiertaEsta ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <div>
                    <div className="text-xs font-semibold text-[#09090B]">
                      {t.nombre || <span className="text-zinc-400">sin nombre</span>}
                    </div>
                    <div className="text-[10px] text-zinc-500 font-mono truncate max-w-[28rem]">{t.metodo} {t.url}</div>
                  </div>
                </button>
                <div className="flex items-center gap-1">
                  {mios.length > 0 && (
                    <Badge variant="outline" className="text-[9px] rounded-sm text-red-600 border-red-200">
                      {mios.length}
                    </Badge>
                  )}
                  <Button variant="ghost" size="sm" data-testid={`borrar-herramienta-${i}`}
                          className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500"
                          onClick={() => cambiar(tools.filter((_, j) => j !== i))}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {abiertaEsta && (
                <div className="border-t border-brand-border p-3 space-y-2 bg-zinc-50/50">
                  <Fila label="Nombre">
                    <Input data-testid={`h-nombre-${i}`} value={t.nombre || ''}
                           onChange={(e) => editar(i, 'nombre', e.target.value)}
                           className="rounded-sm text-xs h-7 font-mono" placeholder="crear_incidencia" />
                  </Fila>
                  <Fila label="Descripcion">
                    <Input data-testid={`h-descripcion-${i}`} value={t.descripcion || ''}
                           onChange={(e) => editar(i, 'descripcion', e.target.value)}
                           className="rounded-sm text-xs h-7"
                           placeholder="Lo unico que el modelo lee para decidir si usarla" />
                  </Fila>
                  <div className="flex flex-wrap gap-2 items-end">
                    <Campo label="Metodo" ancho="w-24">
                      <Select value={t.metodo} onChange={(v) => editar(i, 'metodo', v)} opciones={METODOS} testid={`h-metodo-${i}`} />
                    </Campo>
                    <Campo label="Riesgo" ancho="w-28">
                      <Select value={t.riesgo} onChange={(v) => editar(i, 'riesgo', v)}
                              opciones={RIESGOS.map((r) => r.v)} etiquetas={RIESGOS} testid={`h-riesgo-${i}`} />
                    </Campo>
                    <label className="flex items-center gap-1 text-[10px] text-zinc-600 h-7">
                      <input type="checkbox" data-testid={`h-aprobacion-${i}`}
                             checked={!!t.requiere_aprobacion}
                             onChange={(e) => editar(i, 'requiere_aprobacion', e.target.checked)} />
                      pide aprobacion humana
                    </label>

                    {/* **El tiempo de espera existía y no tenía dónde tocarse.** El default de 30
                        segundos alcanza para casi todo, pero un reporte pesado tarda más y la
                        llamada se cortaba **antes de que el servidor terminara** — con un error
                        de timeout que se lee como «no anda» cuando en realidad andaba y faltaba
                        paciencia. Le pasó a Diego con el libro banco, que tarda 38s. */}
                    <Campo label="Espera (seg)" ancho="w-28">
                      <Input
                        data-testid={`h-timeout-${i}`}
                        type="number" min={1} max={120}
                        value={t.timeout_seg ?? 30}
                        onChange={(e) => editar(i, 'timeout_seg', Math.max(1, Math.min(120, Number(e.target.value) || 30)))}
                        className="rounded-sm text-xs h-7"
                        title="Cuánto se espera la respuesta antes de cortar. Una consulta pesada —un reporte, un rango grande— necesita más que los 30 por omisión. El tope es 120: más que eso es un turno colgado y una persona esperando."
                      />
                    </Campo>
                  </div>
                  <Fila label="URL">
                    <Input data-testid={`h-url-${i}`} value={t.url || ''}
                           onChange={(e) => editar(i, 'url', e.target.value)}
                           className="rounded-sm text-xs h-7 font-mono"
                           placeholder="https://api.ejemplo.com/recurso/{{id}}" />
                  </Fila>
                  <div className="flex flex-wrap gap-2 items-end">
                    <Campo label="Como autentica" ancho="w-52">
                      <Select value={t.auth?.modo || 'ninguna'} testid={`h-auth-modo-${i}`}
                              onChange={(v) => editar(i, 'auth', { ...(t.auth || {}), modo: v })}
                              opciones={MODOS_AUTH} />
                    </Campo>
                    <Campo label="Conexion" ancho="w-40">
                      {/* Elegir de una lista y no escribir el id: un typo aca es un turno que
                          falla con un error de credencial que parece de permisos. */}
                      <Select value={t.auth?.conexion_id || ''} testid={`h-conexion-${i}`}
                              onChange={(v) => editar(i, 'auth', { ...(t.auth || {}), conexion_id: v })}
                              opciones={['', ...conexiones.map((c) => c.conexion_id)]}
                              etiquetas={[{ v: '', t: '(ninguna)' }]} />
                    </Campo>
                  </div>

                  {/* Solo para lo que no es lectura: un GET no cambia nada del otro lado y
                      pedirselo seria burocracia sobre el caso comun. */}
                  {!['GET', 'HEAD'].includes(t.metodo) && (
                    <ConsecuenciasDeLaHerramienta
                      tool={t} indice={i}
                      onCambiar={(campo, valor) =>
                        editar(i, 'consecuencias', { ...(t.consecuencias || {}), [campo]: valor })}
                    />
                  )}

                  <ParametrosDeHerramienta
                    tool={t} indice={i} onCambiar={editarParam}
                    onAgregar={() => editar(i, 'parametros', [...(t.parametros || []), {
                      nombre: '', descripcion: '', tipo: 'texto', ubicacion: 'query',
                      lo_pone: 'modelo', requerido: false,
                    }])}
                    onBorrar={(j) => editar(i, 'parametros', (t.parametros || []).filter((_, m) => m !== j))}
                  />

                  {/* Solo si la definicion esta sana: probar algo que ya se sabe que esta mal
                      devuelve un error de la API que tapa el real. */}
                  {mios.length === 0 && (
                    <ProbadorDeHerramienta tool={t} indice={i} tenantId={tenantId} capsuleId={capsuleId} />
                  )}

                  {mios.length > 0 && (
                    <div data-testid={`errores-${i}`} className="text-[11px] rounded-sm border border-red-200 bg-red-50 p-2 space-y-1">
                      {mios.map((e, k) => (
                        <div key={k} className="text-red-800">
                          <span className="font-mono text-[10px] opacity-60">{e.campo || e.codigo}</span> — {e.mensaje}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* **Las dos puertas, y la primera es la que sirve a más gente.** Pedir un curl que ya
            funcione es pedirle a alguien que ya resolvió el problema; pegar lo que uno tenga
            —la ficha de Postman, la documentación, unas notas— es el caso real. El curl queda
            porque para el que sí lo tiene es el camino más corto. */}
        <PegarLoQueTengas
          tenantId={tenantId}
          capsuleId={capsuleId}
          onListo={(propuesta) => {
            const h = propuesta?.herramienta;
            if (!h) return;
            agregar({
              nombre: h.nombre,
              descripcion: h.descripcion,
              metodo: h.metodo,
              // **La URL va completa, no la ruta suelta.** El validador la exige absoluta —una
              // ruta relativa no resuelve y el error aparecería recién al ejecutar—, así que se
              // compone acá con la base que el mismo asistente dedujo. Es lo mismo que hace
              // `herramientas_del_conector.instanciar` con las que trae el producto.
              url: componerLaUrl(propuesta?.conexion?.base_url, h.ruta),
              parametros: (h.parametros || []).map((x) => ({
                nombre: x.nombre,
                descripcion: x.descripcion,
                ubicacion: x.ubicacion,
                requerido: !!x.requerido,
                valor_por_defecto: x.valor_por_defecto ?? null,
              })),
              auth: { modo: propuesta?.conexion?.auth_modo || 'ninguna',
                      conexion_id: propuesta?.conexion?.nombre_corto || '' },
            });
          }}
        />

        <ImportarCurl tenantId={tenantId} capsuleId={capsuleId} onImportada={agregar} />
      </CardContent>
    </Card>
  );
}

/**
 * Un campo con su etiqueta a la izquierda. Para los que ocupan el ancho completo.
 *
 * **Corregido el 2026-08-22.** La version anterior aceptaba un `ancho` y seguia poniendo la
 * etiqueta en `w-24` con `shrink-0`: en un campo `w-20` la etiqueta sola era mas ancha que el
 * contenedor y tenia prohibido encogerse, asi que se montaba sobre el control de al lado. Los
 * campos angostos ahora usan `Campo`, que pone la etiqueta arriba y no puede cruzarse con nada.
 */
/**
 * Ejecutar la herramienta contra el sistema real, desde la ficha.
 *
 * **Por que.** Hasta aca se podia armar una herramienta que valida perfecto y no anda: la URL
 * existe pero devuelve 404, la credencial es de otra cuenta, el endpoint espera el campo con
 * otro nombre. Nada de eso se ve leyendo el documento — se ve llamando. Sin este boton, la
 * primera llamada de verdad la hace un agente en el turno de una persona.
 *
 * **Y probar no es inofensivo.** Un POST de prueba crea algo de verdad en el sistema del
 * cliente. Las de lectura corren solas; el resto pide confirmar, y el cartel dice **que** va a
 * pasar antes de que pase.
 */
function ProbadorDeHerramienta({ tool, tenantId, capsuleId, indice }) {
  const [valores, setValores] = useState({});
  const [corriendo, setCorriendo] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Solo los que pone el modelo: los del runtime y los de la conexion los completa el motor,
  // y pedirlos aca haria creer que el agente tambien tiene que mandarlos.
  const delModelo = (tool.parametros || []).filter((p) => (p.lo_pone || 'modelo') === 'modelo');

  const correr = async (confirmando) => {
    setCorriendo(true);
    setResultado(null);
    try {
      const { data } = await skillsAPI.probarHerramienta({
        herramienta: tool, params: valores,
        tenant_id: tenantId || '', capsule_id: capsuleId || '',
        confirmo_escritura: !!confirmando,
      });
      setResultado(data);
    } catch (err) {
      setResultado({ ok: false, paso: 'red', mensaje: formatApiError(err) || 'No se pudo probar' });
    } finally {
      setCorriendo(false);
    }
  };

  return (
    <div className="border border-brand-border rounded-sm bg-white" data-testid={`probador-${indice}`}>
      <div className="px-2 py-1 border-b border-brand-border text-[10px] uppercase tracking-[0.1em] text-zinc-500">
        Probar contra el sistema real
      </div>
      <div className="p-2 space-y-2">
        {delModelo.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {delModelo.map((p) => (
              <Campo key={p.nombre} label={p.nombre || '(sin nombre)'} ancho="w-40">
                <Input value={valores[p.nombre] ?? (p.valor_por_defecto || '')}
                       onChange={(e) => setValores({ ...valores, [p.nombre]: e.target.value })}
                       className="rounded-sm text-[11px] h-6 font-mono"
                       placeholder={p.requerido ? 'requerido' : 'opcional'} />
              </Campo>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" data-testid={`probar-herramienta-${indice}`}
                className="h-7 text-[10px] rounded-sm gap-1" onClick={() => correr(false)}
                disabled={corriendo}>
          {corriendo ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
          {/* El boton dice que va a hacer. "Probar" sobre una escritura sugiere que la ejecuta,
              y sobre una lectura sugiere que no — las dos lecturas equivocadas. */}
          {['GET', 'HEAD'].includes(tool.metodo) ? 'Probar' : 'Preparar sin enviar'}
        </Button>

        {/* El preflight: la peticion exacta, tal como saldria. Es lo que permite ver que la
            plantilla resolvio, que credencial se uso y que cuerpo se arma — sin crear nada. */}
        {resultado?.paso === 'preflight' && (
          <div data-testid={`preflight-${indice}`}
               className="text-[11px] rounded-sm border border-blue-200 bg-blue-50 text-blue-900 p-2 space-y-2">
            <div>{resultado.mensaje.replace(/\*\*/g, '')}</div>
            <pre className="bg-[#0A0A0A] text-zinc-300 p-2 rounded-sm text-[10px] overflow-x-auto max-h-56">
{`${resultado.peticion.metodo} ${resultado.peticion.url}`}
{Object.keys(resultado.peticion.query || {}).length
  ? `\n\nQuery: ${JSON.stringify(resultado.peticion.query, null, 2)}` : ''}
{`\n\nHeaders: ${JSON.stringify(resultado.peticion.headers, null, 2)}`}
{resultado.peticion.cuerpo
  ? `\n\nCuerpo: ${JSON.stringify(resultado.peticion.cuerpo, null, 2)}` : ''}
            </pre>
            <Button size="sm" variant="outline" data-testid={`ejecutar-de-verdad-${indice}`}
                    className="h-6 text-[10px] rounded-sm border-blue-400 text-blue-900"
                    onClick={() => correr(true)} disabled={corriendo}>
              Ejecutar de verdad
            </Button>
          </div>
        )}

        {resultado?.paso === 'permiso' && (
          <div data-testid={`sin-permiso-${indice}`}
               className="text-[11px] rounded-sm border border-amber-300 bg-amber-50 text-amber-900 p-2 flex gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" /><div>{resultado.mensaje}</div>
          </div>
        )}

        {resultado?.paso === 'confirmacion' && (
          <div data-testid={`confirmar-${indice}`}
               className="text-[11px] rounded-sm border border-amber-300 bg-amber-50 text-amber-900 p-2 space-y-2">
            <div className="flex gap-2"><ShieldAlert className="w-4 h-4 shrink-0" /><div>{resultado.mensaje}</div></div>
            <Button size="sm" variant="outline" data-testid={`confirmar-boton-${indice}`}
                    className="h-6 text-[10px] rounded-sm border-amber-400 text-amber-900"
                    onClick={() => correr(true)}>
              Ejecutar igual
            </Button>
          </div>
        )}

        {resultado && !['confirmacion', 'preflight', 'permiso'].includes(resultado.paso) && (
          <div data-testid={`resultado-prueba-${indice}`}
               className={`text-[11px] rounded-sm border p-2 space-y-1 ${
                 resultado.ok ? 'border-green-200 bg-green-50 text-green-900'
                              : 'border-red-200 bg-red-50 text-red-900'}`}>
            <div className="flex gap-2">
              {resultado.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
              <div>
                <div>{resultado.mensaje}</div>
                {!resultado.ok && resultado.paso && (
                  <div className="text-[10px] opacity-70">Fallo en: {resultado.paso}</div>
                )}
              </div>
            </div>
            {resultado.respuesta && (
              <pre className="bg-[#0A0A0A] text-zinc-300 p-2 rounded-sm text-[10px] overflow-x-auto max-h-40">
{resultado.respuesta}{resultado.recortada ? '\n… (recortada)' : ''}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function Fila({ label, children }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Label className="text-[10px] text-zinc-600 shrink-0 w-24">{label}</Label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/** Un campo angosto, con la etiqueta arriba. No hay forma de que se solape. */
function Campo({ label, children, ancho = 'w-32' }) {
  return (
    <div className={`${ancho} min-w-0`}>
      <Label className="text-[10px] text-zinc-600 block mb-0.5 truncate">{label}</Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, opciones, etiquetas, testid }) {
  const texto = (v) => (etiquetas || []).find((e) => e.v === v)?.t ?? (v || '—');
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-sm text-xs h-7 border border-brand-border bg-white px-1">
      {opciones.map((o) => <option key={o} value={o}>{texto(o)}</option>)}
    </select>
  );
}

/**
 * Que le hace al mundo esta herramienta.
 *
 * **Por que no alcanza el riesgo.** Un POST que busca pedidos y un POST que emite una orden de
 * pago son el mismo verbo y no se parecen en nada. De esto sale quien tiene que autorizarla, y
 * esa decision la toma la capsula, no el codigo.
 *
 * **`declaradas` es la firma, no una bandera mas.** Sin ella, una herramienta sin ninguna marca
 * seria indistinguible de una que nadie miro — y el silencio pasaria por "inofensiva".
 */
function ConsecuenciasDeLaHerramienta({ tool, indice, onCambiar }) {
  const c = tool.consecuencias || {};
  const marcadas = CONSECUENCIAS.filter((x) => c[x.k]).length;
  return (
    <div className={`border rounded-sm ${c.declaradas ? 'border-brand-border bg-white' : 'border-amber-300 bg-amber-50/60'}`}
         data-testid={`consecuencias-${indice}`}>
      <div className="px-2 pt-2">
        <QueSeConfiguraAca
          testId={`ayuda-consecuencias-${indice}`}
          queEs="Qué le pasa al mundo cuando esta herramienta corre."
          paraQue="No alcanza con saber si es GET o POST. Un POST que busca pedidos y un POST que emite una orden de pago son el mismo verbo y no se parecen en nada. Esto dice de qué tipo es la consecuencia, y de ahí sale quién tiene que autorizarla."
          aQueAfecta={<>A qué controles se le aplican a esta herramienta antes de dejarla correr, y a quién se le pide permiso. Las banderas son fijas —una consecuencia irreversible lo es en cualquier rubro— y <b>a quién pedirle permiso lo decide cada organización</b>.</>}
          ejemplo={<>«Dar de alta un usuario» marca <i>crea acceso</i>. «Emitir un comprobante» marca <i>no se puede deshacer</i>. «Recalcular una lista de precios» marca <i>alcanza a muchos</i>. Una consulta no marca ninguna — pero igual hay que decir que la revisaste.</>}
          ojo={<><b>«Las revisé» es la firma, no una bandera más.</b> Sin tildarla, una herramienta que nadie miró se ve igual que una inofensiva — y el silencio pasa por «no hace nada», que es exactamente como se cuela una orden de pago sin control. Una herramienta que no es GET no se guarda sin esto.</>}
        />
      </div>
      <div className="flex items-center justify-between px-2 py-1 border-b border-brand-border">
        <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
          Que le hace al mundo ({marcadas})
        </span>
        <label className="flex items-center gap-1 text-[10px] text-zinc-700">
          <input type="checkbox" data-testid={`consecuencias-declaradas-${indice}`}
                 checked={!!c.declaradas}
                 onChange={(e) => onCambiar('declaradas', e.target.checked)} />
          Las revise
        </label>
      </div>
      <div className="p-2 space-y-1">
        {!c.declaradas && (
          <p className="text-[10px] text-amber-800">
            Sin esto no se puede guardar: de lo que declares depende quien tiene que autorizarla.
            Si no tiene ninguna, marca <b>Las revise</b> igual.
          </p>
        )}
        {CONSECUENCIAS.map((x) => (
          <label key={x.k} className="flex items-start gap-2 text-[11px] text-zinc-700">
            <input type="checkbox" className="mt-0.5" data-testid={`consecuencia-${x.k}-${indice}`}
                   checked={!!c[x.k]} onChange={(e) => onCambiar(x.k, e.target.checked)} />
            <span><b>{x.t}</b> <span className="text-zinc-500">— {x.d}</span></span>
          </label>
        ))}
      </div>
    </div>
  );
}


function ParametrosDeHerramienta({ tool, indice, onCambiar, onAgregar, onBorrar }) {
  const params = tool.parametros || [];
  return (
    <div className="border border-brand-border rounded-sm bg-white">
      <div className="flex items-center justify-between px-2 py-1 border-b border-brand-border">
        <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">
          Parametros ({params.length})
        </span>
        <Button size="sm" variant="ghost" data-testid={`agregar-param-${indice}`}
                className="h-6 text-[10px]" onClick={onAgregar}>
          <Plus className="w-3 h-3 mr-1" /> agregar
        </Button>
      </div>
      <div className="p-2 space-y-1">
        {params.length === 0 && <p className="text-[10px] text-zinc-400">Ninguno.</p>}
        {params.map((p, j) => (
          // Siete controles en una sola linea que no puede bajar se aplastan hasta cruzarse.
          // `flex-wrap` y un ancho minimo por control: en pantalla angosta baja, no se monta.
          <div key={j} className="flex flex-wrap items-end gap-2 border-b border-brand-border/60 pb-2 last:border-0"
               data-testid={`param-${indice}-${j}`}>
            <Campo label="Nombre" ancho="w-36">
              <Input value={p.nombre || ''} placeholder="titulo"
                     onChange={(e) => onCambiar(indice, j, 'nombre', e.target.value)}
                     className="rounded-sm text-[11px] h-6 font-mono" />
            </Campo>
            <Campo label="Que poner ahi" ancho="flex-1 min-w-[12rem]">
              <Input value={p.descripcion || ''} placeholder="Lo que el modelo necesita saber"
                     onChange={(e) => onCambiar(indice, j, 'descripcion', e.target.value)}
                     className="rounded-sm text-[11px] h-6" />
            </Campo>
            <Campo label="Tipo" ancho="w-24">
              <Select value={p.tipo || 'texto'} opciones={TIPOS}
                      onChange={(v) => onCambiar(indice, j, 'tipo', v)} />
            </Campo>
            <Campo label="Donde va" ancho="w-24">
              <Select value={p.ubicacion || 'query'} opciones={UBICACIONES}
                      onChange={(v) => onCambiar(indice, j, 'ubicacion', v)} />
            </Campo>
            <Campo label="Lo pone" ancho="w-28">
              <Select value={p.lo_pone || 'modelo'} opciones={QUIEN.map((q) => q.v)} etiquetas={QUIEN}
                      onChange={(v) => onCambiar(indice, j, 'lo_pone', v)} />
            </Campo>
            <label className="flex items-center gap-1 text-[10px] text-zinc-600 shrink-0 h-6">
              <input type="checkbox" checked={!!p.requerido}
                     onChange={(e) => onCambiar(indice, j, 'requerido', e.target.checked)} />
              obligatorio
            </label>
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0 text-zinc-400 hover:text-red-500"
                    onClick={() => onBorrar(j)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
